import React from 'react';
import ServiceLandingPage from './ServiceLandingPage';
import { useDocumentSEO } from '../utils/useDocumentSEO';

/**
 * /landing-pages
 * NATIONAL SEO target: high-converting landing pages for ads, infoproducts,
 * launches, service businesses across Brazil.
 */
const LandingPagesPage = () => {
  useDocumentSEO({
    title:
      'Criação de Landing Pages de Alta Conversão | Coutin Agência',
    description:
      'Criação de landing pages profissionais e otimizadas para tráfego pago (Google Ads, Meta Ads). Design exclusivo, copywriting estratégico, integrações e foco em conversão. Atendemos todo o Brasil.',
    canonical: 'https://www.agenciacoutin.com.br/landing-pages',
    ogImage:
      'https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=85&w=1200&auto=format&fit=crop',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Criação de Landing Pages',
      provider: {
        '@type': 'Organization',
        name: 'Coutin Agência',
        url: 'https://www.agenciacoutin.com.br',
        telephone: '+55-45-98806-8508',
      },
      areaServed: { '@type': 'Country', name: 'Brasil' },
      description:
        'Landing pages otimizadas para conversão em campanhas de tráfego pago, lançamentos digitais, infoprodutos e captura de leads.',
      url: 'https://www.agenciacoutin.com.br/landing-pages',
    },
  });

  return (
    <ServiceLandingPage
      gaSource="page-landing-pages"
      showLocalProof={false}
      overline="Landing Pages de Alta Conversão"
      h1="Landing pages que transformam tráfego pago em clientes reais."
      highlightWord="clientes reais"
      subtitle="Desenvolvemos landing pages estratégicas para campanhas de Google Ads, Meta Ads, lançamentos digitais, infoprodutos e captura de leads B2B. Design exclusivo, copy persuasiva, carregamento rápido e foco total na conversão."
      whatsappMessage="Olá! Vim pela página de landing pages e gostaria de um orçamento. Objetivo da campanha (vendas, leads, lançamento, outro):"
      heroImage="https://images.unsplash.com/photo-1559136555-9303baea8ebd?q=85&w=1200&auto=format&fit=crop"
      benefits={[
        { value: '<2s', label: 'Carregamento Rápido' },
        { value: 'A/B', label: 'Testes Inclusos' },
        { value: 'CRO', label: 'Otimizada Conversão' },
        { value: '7 Dias', label: 'Entrega Média' },
      ]}
      intro={[
        'Investir em tráfego pago (Google Ads, Meta Ads, TikTok Ads) sem uma landing page bem construída é jogar dinheiro fora. A diferença entre uma landing genérica e uma landing estratégica pode multiplicar sua taxa de conversão em 3x, 5x ou até 10x — e é exatamente esse trabalho que a Coutin entrega para empresas em todo o Brasil.',
        'Cada landing que criamos passa por 4 etapas: pesquisa do público e da oferta, copywriting persuasivo, design exclusivo (nada de templates) e teste A/B no lançamento. O resultado é uma página que carrega rápido, gera confiança e leva o visitante à ação com clareza.',
        'Trabalhamos com tecnologias modernas e otimização avançada para garantir tempo de carregamento abaixo de 2 segundos, score 90+ no Google PageSpeed, integrações nativas com seu CRM, e-mail marketing e ferramentas de tracking.',
      ]}
      blocks={[
        {
          h2: 'Tipos de landing page que desenvolvemos',
          list: [
            'Captura de leads para infoprodutos',
            'Lançamentos digitais (sequência completa)',
            'Promoção de cursos e webinars',
            'Páginas para profissionais liberais (advogados, médicos, coaches)',
            'Lojas de produto único (Direct Response)',
            'Pré-venda e listas de espera',
            'Páginas de obrigado e thank you pages com remarketing',
            'Funis completos (topo, meio e fundo de funil)',
          ],
        },
        {
          h2: 'Tecnologias e integrações nativas',
          subtitle:
            'Suas landings serão integradas com as ferramentas que você já usa — sem dor de cabeça.',
          list: [
            'Google Ads e Google Tag Manager',
            'Meta Pixel (Facebook e Instagram Ads)',
            'TikTok Pixel e LinkedIn Insight Tag',
            'CRMs (Pipedrive, HubSpot, RD Station, ActiveCampaign)',
            'E-mail marketing (Mailchimp, ConvertKit, Lead Lovers)',
            'Plataformas de pagamento (Stripe, Hotmart, Kiwify, Eduzz)',
            'WhatsApp Business API',
            'Google Sheets, Notion e Airtable',
          ],
        },
        {
          h2: 'O método que aplicamos em cada landing',
          paragraphs: [
            'Antes de desenhar a primeira tela, fazemos um briefing profundo sobre sua oferta, seu público e sua concorrência. Pesquisamos as objeções reais que seu cliente tem antes de comprar e desenhamos a página inteira para neutralizá-las. Isso é o que separa uma landing de R$ 500 de uma landing que escala uma campanha de R$ 100.000.',
            'Em seguida, escrevemos a copy estratégica — usando frameworks consagrados de marketing direto (AIDA, PAS, antes/depois/ponte). Só então o design entra em ação, sempre exclusivo, com o objetivo único de guiar o olho do visitante até o botão de conversão.',
            'No lançamento, configuramos eventos de conversão no Google Analytics 4, integramos com seu pixel de remarketing e oferecemos testes A/B no primeiro mês para refinarmos juntos a página até bater a meta de conversão.',
          ],
        },
        {
          h2: 'Atendemos campanhas em todo o Brasil',
          paragraphs: [
            'Já entregamos landings para clientes em Foz do Iguaçu, Curitiba, São Paulo, Belo Horizonte, Rio de Janeiro, Brasília, Florianópolis, Salvador e muitas outras cidades. Reuniões via Google Meet, comunicação por WhatsApp e entrega via prazo claro definido em contrato.',
          ],
        },
      ]}
    />
  );
};

export default LandingPagesPage;
